import { TaskStatusTag } from '@exam-management/components';
import type { SearchOwnedAndAssignedTasksQuery } from '@exam-management/graphql';
import { UserTypeAvatar } from '@exam-management/user-management';
import { EllipsisText, Flex, type GridColDef } from '@shared/components';
import { dayjs, formatDate } from '@shared/utils';

export type QuestionCreationTask = Pick<
  SearchOwnedAndAssignedTasksQuery['wflSearchOwnedAndAssignedTasks']['tasks'][number],
  | 'dueDate'
  | 'name'
  | 'status'
  | 'code'
  | 'id'
  | 'assignee'
  | 'assigner'
  | 'updatedAt'
  | 'parentTask'
  | 'currentWorkflowStep'
  | 'metadata'
>;

export const columns: GridColDef<QuestionCreationTask>[] = [
  {
    field: 'code',
    headerName: 'Task ID',
    width: 90,
    sortable: false,
    renderCell: (params) => {
      return <EllipsisText variant='body2'>{params.row.code}</EllipsisText>;
    },
  },
  {
    field: 'name',
    headerName: 'Task Name',
    width: 200,
    sortable: false,
    renderCell: (params) => {
      return (
        <EllipsisText variant='body2'>
          {params.row.name ?? params.row.parentTask?.name}
        </EllipsisText>
      );
    },
  },
  {
    field: 'subtaskCount',
    headerName: 'Qty',
    width: 20,
    sortable: false,
    renderCell: (params) => {
      if (params.row.metadata.__typename === 'TaskMetadataWithQuestionCreation') {
        return (
          <EllipsisText variant='body2'>{params.row.metadata.requiredQuestionCount}</EllipsisText>
        );
      }
      return '-';
    },
  },
  {
    field: 'course',
    headerName: 'Course ID',
    flex: 1.5,
    minWidth: 100,
    sortable: false,
    renderCell: (params) => {
      if (params.row.metadata.__typename === 'TaskMetadataWithQuestionCreation') {
        return (
          <EllipsisText variant='body2'>
            {params.row.metadata.qBankStructure?.code ?? '-'}
          </EllipsisText>
        );
      }
      return '-';
    },
  },
  {
    field: 'assigner',
    headerName: 'Assigned by',
    flex: 2,
    sortable: false,
    renderCell: (params) => {
      const fullName = `${params.row.assigner.firstname} ${params.row.assigner.lastname}`;

      return (
        <Flex sx={{ gap: 2, alignItems: 'center' }}>
          <UserTypeAvatar
            fullName={fullName}
            sx={{ width: 32, height: 32 }}
            email={params.row.assigner.email}
          />
          <EllipsisText variant='body2'>{fullName}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'assignee',
    headerName: 'Assignee',
    flex: 2,
    sortable: false,
    renderCell: (params) => {
      const fullName = `${params.row.assignee.firstname} ${params.row.assignee.lastname}`;

      return (
        <Flex sx={{ gap: 2, alignItems: 'center' }}>
          <UserTypeAvatar
            fullName={fullName}
            sx={{ width: 32, height: 32 }}
            email={params.row.assignee.email}
          />
          <EllipsisText variant='body2'>{fullName}</EllipsisText>
        </Flex>
      );
    },
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    width: 120,
    sortable: false,
    valueFormatter: (params) => {
      return formatDate(params.value);
    },
  },
  {
    field: 'updatedAt',
    headerName: 'Last Modified',
    width: 156,
    valueFormatter: (params) => {
      const date = dayjs(params.value);
      return date.isValid() ? date.format('YYYY/MM/DD, HH:mm') : '-';
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    sortable: false,
    renderCell: (params) => {
      return (
        <TaskStatusTag status={params.row.status} name={params.row.currentWorkflowStep.name} />
      );
    },
  },
];
